import React from 'react'
import usePermission from 'hooks/usePermission'

/**
 * ProtectedMenuItem Component
 * Conditionally renders menu items based on user permissions
 * 
 * @param {String|Array} permission - Required permission(s)
 * @param {String} permissionLogic - 'any' or 'all' (default: 'any')
 * @param {String|Array} role - Required role(s) (optional)
 * @param {ReactNode} children - Menu item content
 * @param {ReactNode} fallback - Content to show if no permission (default: null)
 */
const ProtectedMenuItem = ({
  permission,
  permissionLogic = 'any',
  role,
  children,
  fallback = null
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole } = usePermission()

  // Check role first if provided
  if (role && !hasRole(role)) {
    return fallback
  }

  // If no permission required, render children
  if (!permission) {
    return <>{children}</>
  }

  // Check permissions
  let hasAccess = false

  if (Array.isArray(permission)) {
    // Multiple permissions
    if (permissionLogic === 'all') {
      hasAccess = hasAllPermissions(permission)
    } else {
      hasAccess = hasAnyPermission(permission)
    }
  } else {
    // Single permission
    hasAccess = hasPermission(permission)
  }

  return hasAccess ? <>{children}</> : fallback
}

export default ProtectedMenuItem
